import { useEffect, useState } from "react";
import { BaseModal } from "../../../../components/modal";
import { PlanDetailsEditModalWrapper } from "./styled";
import { getPlanService } from "../../../../utils/apis/plansandpricing/getPlan";
import { getPlanFeatures } from "../../../../utils/apis/plansandpricing/getPlanFeatures";
import { updatePlanService } from "../../../../utils/apis/plansandpricing/updatePlan";
import { updatePlanFeaturesService } from "../../../../utils/apis/plansandpricing/updatePlanFeatures";
import Cookies from "universal-cookie";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { Label, P, Span } from "../../../../components/typography/styled";
import { BaseInput } from "../../../../components/form/input/styled";
import { Row } from "../../../../components/flex/styled";
import { BaseButton } from "../../../../components/button/styled";
import { DotLoader } from "react-spinners";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

export const PlanDetailsEditModal = ({
  height,
  width,
  planId,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const cookies = new Cookies();
  const TOKEN = cookies.get("TOKEN");

  const [matches, setMatches] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("details");
  const [formDetails, setFormDetails] = useState({
    title: "",
    upperLimit: "",
    lowerLimit: "",
    creditCostPerEmployee: "",
    canRunPayroll: false,
    canRemitPension: false,
    canRemitStaffOnApplication: false,
  });
  const [features, setFeatures] = useState([]);
  const [featureChanges, setFeatureChanges] = useState({});

  useEffect(() => {
    const handleResize = () => {
      setMatches(window.screen.availWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (!planId || !isOpen) return;

    const loadPlanData = async () => {
      try {
        setIsLoading(true);
        const [planResponse, featuresResponse] = await Promise.all([
          getPlanService(TOKEN, planId),
          getPlanFeatures(TOKEN, planId),
        ]);

        const planData = planResponse?.data ?? planResponse;
        setFormDetails({
          title: planData?.title || "",
          upperLimit: planData?.upperLimit || "",
          lowerLimit: planData?.lowerLimit || "",
          creditCostPerEmployee: planData?.creditCostPerEmployee || "",
          canRunPayroll: planData?.canRunPayroll || false,
          canRemitPension: planData?.canRemitPension || false,
          canRemitStaffOnApplication:
            planData?.canRemitStaffOnApplication || false,
        });
        setFeatures(featuresResponse || []);
        setFeatureChanges({});
        setError(null);
      } catch (err) {
        console.error("Failed to load plan data:", err);
        setError("Failed to load plan data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    loadPlanData();
  }, [TOKEN, planId, isOpen]);

  const handleCloseModal = () => {
    document.body.style.overflow = "auto";
    document.body.style.pointerEvents = "auto";
    setError(null);
    setActiveTab("details");
    setFeatureChanges({});
    onClose();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormDetails((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFeatureChange = (featureId, field, newValue) => {
    setFeatureChanges((prev) => ({
      ...prev,
      [featureId]: {
        ...(prev[featureId] || {}),
        [field]: newValue,
      },
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!planId) return;

    setError(null);
    setIsLoading(true);

    try {
      // Update plan details
      const planPayload = {
        title: formDetails.title,
        creditCostPerEmployee: formDetails.creditCostPerEmployee,
        lowerLimit: Number(formDetails.lowerLimit),
        upperLimit: Number(formDetails.upperLimit),
        canRunPayroll: formDetails.canRunPayroll,
        canRemitPension: formDetails.canRemitPension,
        canRemitStaffOnApplication: formDetails.canRemitStaffOnApplication,
      };

      await updatePlanService(TOKEN, planId, planPayload);

      // Update plan features if there are changes
      const changedFeatureIds = Object.keys(featureChanges);
      if (changedFeatureIds.length > 0) {
        const featuresPayload = {
          features: changedFeatureIds
            .map((featureChangeId) => {
              const change = featureChanges[featureChangeId] || {};
              const feature = features.find((feature) => {
                const id = String(feature.featureDefinitionId || feature.id);
                return id === String(featureChangeId);
              });

              const featureDefinitionId = Number(
                feature?.featureDefinitionId || feature?.id || featureChangeId,
              );

              return {
                featureDefinitionId,
                value: change.value ?? feature?.value ?? "",
                isActive:
                  change.isActive !== undefined
                    ? change.isActive
                    : (feature?.isActive ?? true),
              };
            })
            .filter(
              (feature) =>
                feature.featureDefinitionId !== undefined &&
                !Number.isNaN(feature.featureDefinitionId),
            ),
        };

        if (featuresPayload.features.length > 0) {
          await updatePlanFeaturesService(TOKEN, planId, featuresPayload);
        }
      }

      setIsLoading(false);
      onSuccess?.();
      handleCloseModal();
    } catch (error) {
      setIsLoading(false);
      setError(`Update failed. ${error.message}`);
      console.error("Plan update failed:", error);
    }
  };

  return (
    <BaseModal
      open={isOpen}
      onClose={handleCloseModal}
      className={"plan-details-edit-modal"}
      height={matches ? "auto" : height || "600px"}
      width={matches ? "auto" : width || "60%"}
    >
      <PlanDetailsEditModalWrapper onSubmit={handleSave}>
        <div className="close-modal-button-area">
          <BaseButton
            className="close-modal-button"
            onClick={handleCloseModal}
            type="button"
          >
            <Span>
              <FontAwesomeIcon icon={faXmark} />
            </Span>
          </BaseButton>
        </div>

        <div className="modal-header">
          <legend>Edit Plan Details</legend>
          <P>Update plan information, features, and settings below.</P>
        </div>

        <div className="tab-navigation">
          <button
            type="button"
            className={`tab-button ${activeTab === "details" ? "active" : ""}`}
            onClick={() => setActiveTab("details")}
          >
            Plan Details
          </button>
          <button
            type="button"
            className={`tab-button ${activeTab === "options" ? "active" : ""}`}
            onClick={() => setActiveTab("options")}
          >
            Plan Options
          </button>
          <button
            type="button"
            className={`tab-button ${activeTab === "features" ? "active" : ""}`}
            onClick={() => setActiveTab("features")}
          >
            Features
          </button>
        </div>

        {isLoading && !formDetails.title ? (
          <P>Loading plan data…</P>
        ) : error ? (
          <P style={{ color: "red" }}>{error}</P>
        ) : (
          <>
            {activeTab === "details" && (
              <div className="tab-content">
                <Row tocolumn={true}>
                  <BaseFieldSet>
                    <Label>Title</Label>
                    <BaseInput
                      type="text"
                      name="title"
                      placeholder="Enter plan title"
                      value={formDetails.title}
                      onChange={handleChange}
                      required
                    />
                  </BaseFieldSet>
                  <BaseFieldSet>
                    <Label>Credit Cost Per Employee</Label>
                    <BaseInput
                      type="text"
                      name="creditCostPerEmployee"
                      placeholder="Enter credit cost"
                      value={formDetails.creditCostPerEmployee}
                      onChange={handleChange}
                      required
                    />
                  </BaseFieldSet>
                </Row>
                <Row tocolumn={true}>
                  <BaseFieldSet>
                    <Label>Upper Limit</Label>
                    <BaseInput
                      type="number"
                      name="upperLimit"
                      placeholder="Enter upper limit"
                      value={formDetails.upperLimit}
                      onChange={handleChange}
                      required
                    />
                  </BaseFieldSet>
                  <BaseFieldSet>
                    <Label>Lower Limit</Label>
                    <BaseInput
                      type="number"
                      name="lowerLimit"
                      placeholder="Enter lower limit"
                      value={formDetails.lowerLimit}
                      onChange={handleChange}
                      required
                    />
                  </BaseFieldSet>
                </Row>
              </div>
            )}

            {activeTab === "options" && (
              <div className="tab-content">
                <div className="checkbox-group">
                  <BaseFieldSet className="checkbox-fieldset">
                    <Label>
                      <BaseInput
                        type="checkbox"
                        name="canRunPayroll"
                        checked={formDetails.canRunPayroll}
                        onChange={handleChange}
                      />
                      <span>Can Run Payroll</span>
                    </Label>
                  </BaseFieldSet>
                  <BaseFieldSet className="checkbox-fieldset">
                    <Label>
                      <BaseInput
                        type="checkbox"
                        name="canRemitPension"
                        checked={formDetails.canRemitPension}
                        onChange={handleChange}
                      />
                      <span>Can Remit Pension</span>
                    </Label>
                  </BaseFieldSet>
                  <BaseFieldSet className="checkbox-fieldset">
                    <Label>
                      <BaseInput
                        type="checkbox"
                        name="canRemitStaffOnApplication"
                        checked={formDetails.canRemitStaffOnApplication}
                        onChange={handleChange}
                      />
                      <span>Can Remit Staff on Application</span>
                    </Label>
                  </BaseFieldSet>
                </div>
              </div>
            )}

            {activeTab === "features" && (
              <div className="tab-content">
                <div className="features-list">
                  {features && features.length > 0 ? (
                    features.map((feature) => {
                      const featureId = String(
                        feature.featureDefinitionId || feature.id,
                      );
                      const currentValue =
                        featureChanges[featureId]?.value !== undefined
                          ? featureChanges[featureId].value
                          : feature.value;
                      const currentIsActive =
                        featureChanges[featureId]?.isActive !== undefined
                          ? featureChanges[featureId].isActive
                          : (feature.isActive ?? true);

                      return (
                        <BaseFieldSet key={featureId} className="feature-item">
                          <div className="feature-main">
                            <Label>{feature.name || feature.code}</Label>
                            <BaseInput
                              type="text"
                              value={currentValue ?? ""}
                              onChange={(e) =>
                                handleFeatureChange(
                                  featureId,
                                  "value",
                                  e.target.value,
                                )
                              }
                              placeholder="Enter feature value"
                            />
                          </div>
                          <div className="feature-toggle">
                            <Label className="toggle-label">
                              <BaseInput
                                type="checkbox"
                                checked={Boolean(currentIsActive)}
                                onChange={(e) =>
                                  handleFeatureChange(
                                    featureId,
                                    "isActive",
                                    e.target.checked,
                                  )
                                }
                              />
                              <span>Enabled</span>
                            </Label>
                          </div>
                        </BaseFieldSet>
                      );
                    })
                  ) : (
                    <P>No features found for this plan.</P>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        <div className="submit-button-area">
          <BaseButton
            type="button"
            onClick={handleCloseModal}
            backgroundcolor="#f0f0f0"
            color="var(--color-text)"
            width={matches ? "-webkit-fill-available" : "fit-content"}
          >
            Cancel
          </BaseButton>
          <BaseButton
            type="submit"
            backgroundcolor={"var(--color-primary)"}
            width={matches ? "-webkit-fill-available" : "fit-content"}
          >
            {isLoading ? (
              <DotLoader size={20} color="white" className="dotLoader" />
            ) : (
              <Span>Save Changes</Span>
            )}
          </BaseButton>
        </div>
      </PlanDetailsEditModalWrapper>
    </BaseModal>
  );
};
